import { ePaymentMethod } from "../../../enums/billing-management/enum-payment-method.enum";
import { BaseModel } from "shared-domains/domains/base.model";
import { fromTimestamp, toTimestamp } from "shared-domains/utilities/grpc-convert.helper";
import { eTopupOrderStatus } from "../../../enums/billing-management/enum-topup-order-status";

export class TopupOrder extends BaseModel {
    public TopupOrderId = 0;
    public WalletInfoId = 0;
    public OrderCode = "";
    public UserRequestId = 0;
    public PaymentMethodId: number = ePaymentMethod.PAYMENT_METHOD_BANK_TRANSFER;
    public PaymentMethod = "Bank Transfer"; //Ko trong request, chỉ để hiển thị
    public RequestedAmount = 0;
    public ActualAmount = 0;
    public Fee = 0;
    public CurrencyId = 1;
    public CurrencyCode = "USD";
    public EvidenceUrl = "";
    public TransactionRef = "";
    public TopupOrderStatusId: number = eTopupOrderStatus.TOPUP_ORDER_STATUS_PENDING;
    public TopupOrderStatus = "Pending"; //Ko trong request, chỉ để hiển thị
    public ApprovedBy = 0;
    public ApprovedByDisplayName = "";
    public ApprovedAt: Date = new Date();
    public UserNote = "";
    public AdminNote = "";
    public TransactionLedgerId = "";

    // ── Multi-provider top-up (backend-authoritative; fee/status resolved server-side) ──
    public ProviderCode = "";
    public ProviderTransactionId = "";
    public PaymentUrl = "";
    public QrPayload = "";
    public RejectReason = "";
    public PaymentStatusId = 0;
    public PostingStatusId = 0;
    public ReconciliationStatusId = 0;

    constructor(dto?: any) {
        super();
        if (!dto) return;
        this.TopupOrderId = Number(dto.topupOrderId);
        this.WalletInfoId = Number(dto.walletInfoId);
        this.OrderCode = dto.orderCode;
        this.UserRequestId = Number(dto.userRequestId);
        this.PaymentMethodId = dto.paymentMethodId;
        this.RequestedAmount = Number(dto.requestedAmount) || 0;
        this.ActualAmount = Number(dto.actualAmount) || 0;
        this.Fee = Number(dto.fee) || 0;
        this.CurrencyId = Number(dto.currencyId);
        this.CurrencyCode = dto.currencyCode;
        this.EvidenceUrl = dto.evidenceUrl;
        this.TransactionRef = dto.transactionRef;
        this.TopupOrderStatusId = dto.topupOrderStatusId;
        this.ApprovedBy = Number(dto.approvedBy);
        this.ApprovedByDisplayName = dto.approvedByDisplayName;
        const approvedAt = fromTimestamp(dto.approvedAt);
        if (approvedAt) this.ApprovedAt = approvedAt;
        this.UserNote = dto.userNote;
        this.AdminNote = dto.adminNote;
        this.TransactionLedgerId = dto.transactionLedgerId;
        this.Active = dto.active;
        this.Archive = dto.archive;
        this.Description = dto.description;
        // Multi-provider fields.
        this.ProviderCode = dto.providerCode ?? "";
        this.ProviderTransactionId = dto.providerTransactionId ?? "";
        this.PaymentUrl = dto.paymentUrl ?? "";
        this.QrPayload = dto.qrPayload ?? "";
        this.RejectReason = dto.rejectReason ?? "";
        this.PaymentStatusId = dto.paymentStatusId ?? 0;
        this.PostingStatusId = dto.postingStatusId ?? 0;
        this.ReconciliationStatusId = dto.reconciliationStatusId ?? 0;
        const createdAt = fromTimestamp(dto.createdAt);
        if (createdAt) this.CreatedAt = createdAt;
        const updatedAt = fromTimestamp(dto.updatedAt);
        if (updatedAt) this.UpdatedAt = updatedAt;
    }

    /** Build a TopupOrder from a gRPC TopupOrderDTO (camelCase → PascalCase; money strings → number). */
    static fromDto(dto?: any): TopupOrder {
        return new TopupOrder(dto);
    }

    /** Convert to a gRPC TopupOrderDTO plain object (PascalCase → camelCase; money numbers → string, Date → Timestamp). */
    toDto(): any {
        return {
            topupOrderId: this.TopupOrderId,
            walletInfoId: this.WalletInfoId,
            orderCode: this.OrderCode ?? "",
            userRequestId: this.UserRequestId,
            paymentMethodId: this.PaymentMethodId,
            requestedAmount: String(this.RequestedAmount ?? 0),
            actualAmount: String(this.ActualAmount ?? 0),
            fee: String(this.Fee ?? 0),
            currencyId: this.CurrencyId,
            currencyCode: this.CurrencyCode ?? "",
            evidenceUrl: this.EvidenceUrl ?? "",
            transactionRef: this.TransactionRef ?? "",
            topupOrderStatusId: this.TopupOrderStatusId,
            approvedBy: this.ApprovedBy,
            approvedByDisplayName: this.ApprovedByDisplayName ?? "",
            approvedAt: toTimestamp(this.ApprovedAt ? new Date(this.ApprovedAt) : null),
            userNote: this.UserNote ?? "",
            adminNote: this.AdminNote ?? "",
            transactionLedgerId: this.TransactionLedgerId ?? "",
            active: this.Active,
            archive: this.Archive,
            description: this.Description ?? "",
        };
    }

    shallowCopy(): TopupOrder {
        const shallowObj = new TopupOrder();

        shallowObj.Active = this.Active;
        shallowObj.Archive = this.Archive;
        shallowObj.Description = this.Description;
        shallowObj.CreatedAt = this.CreatedAt;
        shallowObj.UpdatedAt = this.UpdatedAt;

        shallowObj.TopupOrderId = this.TopupOrderId;
        shallowObj.WalletInfoId = this.WalletInfoId;
        shallowObj.OrderCode = this.OrderCode;
        shallowObj.UserRequestId = this.UserRequestId;
        shallowObj.PaymentMethodId = this.PaymentMethodId;
        shallowObj.PaymentMethod = this.PaymentMethod;
        shallowObj.RequestedAmount = this.RequestedAmount;
        shallowObj.ActualAmount = this.ActualAmount;
        shallowObj.Fee = this.Fee;
        shallowObj.CurrencyId = this.CurrencyId;
        shallowObj.CurrencyCode = this.CurrencyCode;
        shallowObj.EvidenceUrl = this.EvidenceUrl;
        shallowObj.TransactionRef = this.TransactionRef;
        shallowObj.TopupOrderStatusId = this.TopupOrderStatusId;
        shallowObj.TopupOrderStatus = this.TopupOrderStatus;
        shallowObj.ApprovedBy = this.ApprovedBy;
        shallowObj.ApprovedByDisplayName = this.ApprovedByDisplayName;
        shallowObj.ApprovedAt = this.ApprovedAt;
        shallowObj.UserNote = this.UserNote;
        shallowObj.AdminNote = this.AdminNote;
        shallowObj.TransactionLedgerId = this.TransactionLedgerId;

        shallowObj.ProviderCode = this.ProviderCode;
        shallowObj.ProviderTransactionId = this.ProviderTransactionId;
        shallowObj.PaymentUrl = this.PaymentUrl;
        shallowObj.QrPayload = this.QrPayload;
        shallowObj.RejectReason = this.RejectReason;
        shallowObj.PaymentStatusId = this.PaymentStatusId;
        shallowObj.PostingStatusId = this.PostingStatusId;
        shallowObj.ReconciliationStatusId = this.ReconciliationStatusId;

        return shallowObj;
    }

    copyData(obj: TopupOrder) {
        if (obj == undefined) return;
        this.Active = obj.Active;
        this.Archive = obj.Archive;
        this.Description = obj.Description;
        this.CreatedAt = obj.CreatedAt;
        this.UpdatedAt = obj.UpdatedAt;

        this.TopupOrderId = obj.TopupOrderId;
        this.WalletInfoId = obj.WalletInfoId;
        this.OrderCode = obj.OrderCode;
        this.UserRequestId = obj.UserRequestId;
        this.PaymentMethodId = obj.PaymentMethodId;
        this.PaymentMethod = obj.PaymentMethod;
        this.RequestedAmount = obj.RequestedAmount;
        this.ActualAmount = obj.ActualAmount;
        this.Fee = obj.Fee;
        this.CurrencyId = obj.CurrencyId;
        this.CurrencyCode = obj.CurrencyCode;
        this.EvidenceUrl = obj.EvidenceUrl;
        this.TransactionRef = obj.TransactionRef;
        this.TopupOrderStatusId = obj.TopupOrderStatusId;
        this.TopupOrderStatus = obj.TopupOrderStatus;
        this.ApprovedBy = obj.ApprovedBy;
        this.ApprovedByDisplayName = obj.ApprovedByDisplayName;
        this.ApprovedAt = obj.ApprovedAt;
        this.UserNote = obj.UserNote;
        this.AdminNote = obj.AdminNote;
        this.TransactionLedgerId = obj.TransactionLedgerId;

        this.ProviderCode = obj.ProviderCode;
        this.ProviderTransactionId = obj.ProviderTransactionId;
        this.PaymentUrl = obj.PaymentUrl;
        this.QrPayload = obj.QrPayload;
        this.RejectReason = obj.RejectReason;
        this.PaymentStatusId = obj.PaymentStatusId;
        this.PostingStatusId = obj.PostingStatusId;
        this.ReconciliationStatusId = obj.ReconciliationStatusId;
    }


    /**
     * SL-771: trả về KHOÁ i18n, không phải chữ hiển thị. Lưới pipe giá trị này qua `| transloco`, nên khi
     * getter trả chuỗi English hardcode ("Bank Transfer"...) transloco không tìm thấy khoá và in nguyên
     * văn ⇒ cột không đổi theo ngôn ngữ.
     *
     * Đơn multi-provider mang `ProviderCode` (nguồn chuẩn, cùng namespace khoá mà popup tạo lệnh đã dùng);
     * `PaymentMethodId` chỉ mô tả đủ 4 phương thức của luồng legacy nên chỉ dùng khi không có ProviderCode.
     */
    public getPaymentMethod() {
        this.PaymentMethod = this.ProviderCode
            ? `wallet-system.payment-provider.${this.ProviderCode}`
            : TopupOrder.legacyPaymentMethodKey(this.PaymentMethodId);
        return this.PaymentMethod;
    }

    private static legacyPaymentMethodKey(paymentMethodId: number): string {
        switch (paymentMethodId) {
            case ePaymentMethod.PAYMENT_METHOD_BANK_TRANSFER:
                return "wallet-system.payment-provider.BankTransfer";
            case ePaymentMethod.PAYMENT_METHOD_PAYPAL:
                return "wallet-system.payment-provider.Paypal";
            case ePaymentMethod.PAYMENT_METHOD_PINGPONG:
                return "wallet-system.payment-provider.Pingpong";
            case ePaymentMethod.PAYMENT_METHOD_CREDIT:
                return "wallet-system.payment-provider.Credit";
            default:
                return "wallet-system.payment-provider.Unknown";
        }
    }

    public getPaymentMethodStyle() {
        switch (this.PaymentMethodId) {
            case ePaymentMethod.PAYMENT_METHOD_BANK_TRANSFER:
                return "border border-accent-600 text-accent-600 dark:border-accent-500 dark:text-accent-500";
            case ePaymentMethod.PAYMENT_METHOD_PAYPAL:
                return "border border-accent-600 text-accent-600 dark:border-accent-500 dark:text-accent-500";
            case ePaymentMethod.PAYMENT_METHOD_PINGPONG:
                return "border border-accent-600 text-accent-600 dark:border-accent-500 dark:text-accent-500";
            case ePaymentMethod.PAYMENT_METHOD_CREDIT:
                return "border border-accent-600 text-accent-600 dark:border-accent-500 dark:text-accent-500";
        }
    }

    /** SL-771: trả về KHOÁ i18n — xem ghi chú ở <see cref="getPaymentMethod"/>. */
    public getTopupOrderStatus() {
        this.TopupOrderStatus = `wallet-system.topup-order-status.${TopupOrder.statusName(this.TopupOrderStatusId)}`;
        return this.TopupOrderStatus;
    }

    private static statusName(topupOrderStatusId: number): string {
        switch (topupOrderStatusId) {
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_PENDING:
                return "Pending";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_APPROVED:
                return "Approved";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_REJECTED:
                return "Rejected";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_CANCELLED:
                return "Cancelled";
            // Multi-provider workflow states.
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_CREATED:
                return "Created";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_PENDING_PAYMENT:
                return "PendingPayment";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_PAID_PENDING_POSTING:
                return "PaidPendingPosting";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_POSTED:
                return "Posted";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_FAILED:
                return "Failed";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_EXPIRED:
                return "Expired";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_REFUND_PENDING:
                return "RefundPending";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_REFUNDED:
                return "Refunded";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_RECONCILIATION_REQUIRED:
                return "ReconciliationRequired";
            default:
                return "Unknown";
        }
    }

    public getTopupOrderStatusStyle() {
        switch (this.TopupOrderStatusId) {
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_PENDING:
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_CREATED:
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_PENDING_PAYMENT:
                return "bg-yellow-200 text-yellow-800 dark:bg-yellow-600 dark:text-yellow-50";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_PAID_PENDING_POSTING:
                return "bg-blue-200 text-blue-800 dark:bg-blue-600 dark:text-blue-50";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_APPROVED:
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_POSTED:
                return "bg-green-200 text-green-800 dark:bg-green-600 dark:text-green-50";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_REJECTED:
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_FAILED:
                return "bg-red-200 text-red-800 dark:bg-red-600 dark:text-red-50";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_RECONCILIATION_REQUIRED:
                return "bg-orange-200 text-orange-800 dark:bg-orange-600 dark:text-orange-50";
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_REFUND_PENDING:
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_REFUNDED:
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_EXPIRED:
            case eTopupOrderStatus.TOPUP_ORDER_STATUS_CANCELLED:
                return "bg-accent-200 text-accent-800 dark:bg-accent-600 dark:text-accent-50";
        }
    }
}

 export interface TopupOrderRequest {
    Active: boolean;
    Archive: boolean;
    TopupOrderId: number;
    WalletInfoId: number;
    OrderCode: string;
    UserRequestId: number;
    PaymentMethodId: number;
    RequestedAmount: number;
    ActualAmount: number;
    Fee: number;
    CurrencyId: number;
    CurrencyCode: string;
    EvidenceUrl: string;
    TransactionRef: string;
    TopupOrderStatusId: number;
    ApprovedBy: number;
    ApprovedByDisplayName: string;
    ApprovedAt: Date;
    UserNote: string;
    AdminNote: string;
}